/**
 * Pointer-driven drag for DOM cards. Mirrors the Three.js drag.ts patterns:
 * pointer capture, movement threshold to distinguish click from drag, and
 * a click/double-click discriminator on pointerup.
 *
 * The renderer owns the lift/origin tracking - this layer just emits events.
 */
export interface ClassicDragHooks {
  /** the felt root element - pointer events are bound here */
  root: HTMLElement;
  /** find the card element under a pointer event (or its ancestor card div) */
  pickAt(target: EventTarget | null): HTMLElement | null;
  /** return the cards that should drag together when a card is picked */
  getDragStack(picked: HTMLElement): HTMLElement[];
  /** called every move (after threshold) with the lead card's current pixel
   *  position relative to root */
  onDragMove(lead: HTMLElement, x: number, y: number): void;
  /** called once at drop. lead is the lead card, x/y are its current pos
   *  relative to root. Return true to commit, false to snap back. */
  onDrop(lead: HTMLElement, x: number, y: number): boolean;
  /** dragging started (after threshold). Lift visual cues go here. */
  onDragStart?(stack: HTMLElement[]): void;
  /** dragging cancelled (drop returned false, or pointer cancelled). */
  onCancel(stack: HTMLElement[]): void;
  /** simple click on a card (no drag). */
  onClick(picked: HTMLElement): void;
  /** double-click on a card. */
  onDoubleClick?(picked: HTMLElement): void;
  /** pointer-down before a stack is determined (used to halt animations). */
  onPickStart?(): void;
  dragThresholdPx?: number;
  doubleClickMs?: number;
}

export function attachClassicDrag(opts: ClassicDragHooks): () => void {
  const threshold = opts.dragThresholdPx ?? 6;
  const doubleClickMs = opts.doubleClickMs ?? 300;

  let stack: HTMLElement[] | null = null;
  let lead: HTMLElement | null = null;
  let pointerStart: { x: number; y: number } | null = null;
  let leadOrigin: { x: number; y: number } | null = null;
  let dragging = false;
  let activePointerId: number | null = null;
  let lastClickEl: HTMLElement | null = null;
  let lastClickTime = 0;

  function readOrigin(el: HTMLElement): { x: number; y: number } {
    return {
      x: parseFloat(el.style.left || '0'),
      y: parseFloat(el.style.top || '0'),
    };
  }

  function onPointerDown(e: PointerEvent) {
    if (activePointerId !== null) return;
    const picked = opts.pickAt(e.target);
    if (!picked) return;
    opts.onPickStart?.();
    activePointerId = e.pointerId;
    pointerStart = { x: e.clientX, y: e.clientY };
    dragging = false;
    lead = picked;
    stack = opts.getDragStack(picked);
    leadOrigin = readOrigin(picked);
    try { picked.setPointerCapture(e.pointerId); } catch {}
    e.preventDefault();
  }

  function onPointerMove(e: PointerEvent) {
    if (e.pointerId !== activePointerId) return;
    if (!lead || !pointerStart || !leadOrigin) return;
    const dx = e.clientX - pointerStart.x;
    const dy = e.clientY - pointerStart.y;
    if (!dragging) {
      if (Math.hypot(dx, dy) < threshold) return;
      if (!stack || stack.length === 0) return;
      dragging = true;
      opts.onDragStart?.(stack);
    }
    const x = leadOrigin.x + dx;
    const y = leadOrigin.y + dy;
    opts.onDragMove(lead, x, y);
  }

  function reset() {
    stack = null;
    lead = null;
    pointerStart = null;
    leadOrigin = null;
    dragging = false;
    activePointerId = null;
  }

  function onPointerUp(e: PointerEvent) {
    if (e.pointerId !== activePointerId) return;
    if (!lead) { reset(); return; }
    try { lead.releasePointerCapture(e.pointerId); } catch {}
    if (!dragging) {
      const now = performance.now();
      const isDouble =
        lead === lastClickEl && now - lastClickTime < doubleClickMs;
      if (isDouble && opts.onDoubleClick) {
        opts.onDoubleClick(lead);
        lastClickEl = null;
        lastClickTime = 0;
      } else {
        opts.onClick(lead);
        lastClickEl = lead;
        lastClickTime = now;
      }
      reset();
      return;
    }
    if (!stack) { reset(); return; }
    const x = parseFloat(lead.style.left || '0');
    const y = parseFloat(lead.style.top || '0');
    const ok = opts.onDrop(lead, x, y);
    if (!ok) opts.onCancel(stack);
    reset();
  }

  function onPointerCancel(e: PointerEvent) {
    if (e.pointerId !== activePointerId) return;
    if (stack && dragging) opts.onCancel(stack);
    reset();
  }

  opts.root.addEventListener('pointerdown', onPointerDown);
  opts.root.addEventListener('pointermove', onPointerMove);
  opts.root.addEventListener('pointerup', onPointerUp);
  opts.root.addEventListener('pointercancel', onPointerCancel);

  return () => {
    opts.root.removeEventListener('pointerdown', onPointerDown);
    opts.root.removeEventListener('pointermove', onPointerMove);
    opts.root.removeEventListener('pointerup', onPointerUp);
    opts.root.removeEventListener('pointercancel', onPointerCancel);
  };
}
